import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { toast } from "sonner";
import { Check, X, MapPin, Hash, Building2, ArrowLeftRight } from "lucide-react";

const PAIR_LABEL = { "IT-DP": "PAN ↔ Darpan", "IT-FC": "PAN ↔ FCRA", "DP-FC": "Darpan ↔ FCRA" };

function SideCard({ side, other }) {
  if (!side) return null;
  const nameMatch = other && side.name && other.name &&
    side.name.toLowerCase().replace(/[^a-z]/g, "") === other.name.toLowerCase().replace(/[^a-z]/g, "");
  const pinMatch = other && side.pin && other.pin && side.pin === other.pin;
  return (
    <div className="flex-1 rounded-xl border border-slate-800 bg-slate-950/50 p-4">
      <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2">{side.registry}</div>
      <div className={`text-sm font-semibold mb-3 ${nameMatch ? "text-emerald-700" : "text-slate-200"}`}>
        <Building2 className="inline w-3.5 h-3.5 mr-1.5 -mt-0.5" />{side.name || "—"}
      </div>
      <div className="space-y-1.5 text-[13px] text-slate-400">
        <div className="flex gap-2"><Hash className="w-3.5 h-3.5 mt-0.5 text-slate-600" /><span className="font-mono">{side.id}</span></div>
        <div className="flex gap-2"><MapPin className="w-3.5 h-3.5 mt-0.5 text-slate-600" /><span>{side.address || "—"}</span></div>
        <div className={`flex gap-2 ${pinMatch ? "text-emerald-600" : ""}`}><span className="font-mono text-slate-600 w-3.5 text-center">#</span><span className="font-mono">PIN {side.pin || "—"}</span></div>
      </div>
    </div>
  );
}

export default function ReviewQueue() {
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [flag, setFlag] = useState("givedo");
  const [counts, setCounts] = useState({ givedo: 0, total: 0 });

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/v1/review/queue", { params: { flag } });
      setQueue(res.data.queue);
      setCounts({ givedo: res.data.givedo_count, total: res.data.total });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [flag]);

  const decide = async (item, decision) => {
    const key = `${item.pair_type}-${item.left_id}-${item.right_id}`;
    setBusy(key);
    try {
      await api.post("/v1/review/decision", {
        pair_type: item.pair_type, left_id: item.left_id, right_id: item.right_id, decision,
      });
      toast.success(decision === "accept" ? "Link accepted & verified" : "Link rejected");
      setQueue((q) => q.filter((x) => `${x.pair_type}-${x.left_id}-${x.right_id}` !== key));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Admin · human-in-the-loop"
        title="Link Review Queue"
        subtitle="Compare both registry records side by side, then accept or reject the proposed link. Decisions are permanent and applied on every mapping run."
      >
        <span className="rounded-full border border-slate-700 px-3 py-1.5 font-mono text-xs text-slate-300">{queue.length} shown</span>
      </PageHeader>

      <div className="mb-5 flex flex-wrap gap-2" data-testid="review-queue-filters">
        <button
          data-testid="review-filter-givedo"
          onClick={() => setFlag("givedo")}
          className={`rounded-full border px-4 py-1.5 text-xs font-medium transition-colors duration-150 ${
            flag === "givedo" ? "border-amber-400 bg-amber-50 text-amber-800" : "border-slate-700 text-slate-400 hover:text-slate-200"
          }`}
        >
          give.do cross-state ({counts.givedo})
        </button>
        <button
          data-testid="review-filter-all"
          onClick={() => setFlag("")}
          className={`rounded-full border px-4 py-1.5 text-xs font-medium transition-colors duration-150 ${
            flag === "" ? "border-blue-500 bg-blue-500/15 text-blue-300" : "border-slate-700 text-slate-400 hover:text-slate-200"
          }`}
        >
          All pending ({counts.total.toLocaleString()})
        </button>
      </div>

      {loading ? (
        <div className="font-mono text-xs text-slate-500">Loading queue…</div>
      ) : queue.length === 0 ? (
        <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-10 text-center text-slate-400">
          Queue is clear — no links awaiting review.
        </div>
      ) : (
        <div className="space-y-4">
          {queue.map((item) => {
            const key = `${item.pair_type}-${item.left_id}-${item.right_id}`;
            return (
              <div key={key} data-testid={`review-link-item-${key}`} className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5 animate-fade-up">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                  <span className="rounded-md border border-slate-700 px-2.5 py-1 font-mono text-[11px] text-slate-300">{PAIR_LABEL[item.pair_type]}</span>
                  {item.reason && <span className="rounded-md bg-amber-50 px-2.5 py-1 text-[11px] text-amber-700">reason: {item.reason}</span>}
                </div>

                <div className="flex flex-col sm:flex-row items-stretch gap-3">
                  <SideCard side={item.left} other={item.right} />
                  <div className="flex items-center justify-center text-slate-600"><ArrowLeftRight className="w-5 h-5" /></div>
                  <SideCard side={item.right} other={item.left} />
                </div>

                <div className="mt-4 flex flex-wrap items-center gap-3">
                  <button
                    data-testid={`accept-link-btn-${key}`}
                    disabled={busy === key}
                    onClick={() => decide(item, "accept")}
                    className="inline-flex items-center gap-2 rounded-full bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-slate-950 hover:bg-emerald-400 disabled:opacity-50 transition-colors duration-150"
                  >
                    <Check className="w-4 h-4" /> Accept link
                  </button>
                  <button
                    data-testid={`reject-link-btn-${key}`}
                    disabled={busy === key}
                    onClick={() => decide(item, "reject")}
                    className="inline-flex items-center gap-2 rounded-full border border-rose-300 bg-rose-50 px-5 py-2.5 text-sm font-semibold text-rose-700 hover:bg-rose-100 disabled:opacity-50 transition-colors duration-150"
                  >
                    <X className="w-4 h-4" /> Reject
                  </button>
                  {item.evidence?.name_similarity != null && (
                    <span className="font-mono text-[11px] text-slate-500">
                      name sim {item.evidence.name_similarity} · addr sim {item.evidence.address_similarity}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
